/**
 * IndexedDB-backed AsyncStorage adapter for the React Query persister.
 *
 * `createAsyncStoragePersister` only needs an object shaped like
 * `{ getItem, setItem, removeItem }` whose methods may return promises. We back
 * that with a single IndexedDB object store, storing one serialized
 * `PersistedClient` blob per partition key (see `partition-key.ts`). Different
 * (user, org, partner) contexts write to different keys in the same store, so
 * they never overwrite each other.
 *
 * Every method is wrapped in try/catch: if IndexedDB is unavailable (Safari
 * private mode, locked-down enterprise policy, storage partitioning quirks) the
 * adapter degrades to "no persistence" - reads return null, writes no-op - so
 * the app still boots and fetches normally. It must never throw, because it is
 * wired at App module scope alongside the QueryClient.
 */

import { openDB, type IDBPDatabase } from 'idb'

const DB_NAME = 'raven-admin-query-cache'
const STORE = 'kv'
const DB_VERSION = 1

let dbPromise: Promise<IDBPDatabase> | null = null

function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE)
        }
      },
    }).catch((error) => {
      // Reset so a later call can retry; surface as a rejected promise the
      // callers below already catch.
      dbPromise = null
      throw error
    })
  }
  return dbPromise
}

export interface IdbAsyncStorage {
  getItem: (key: string) => Promise<string | null>
  setItem: (key: string, value: string) => Promise<void>
  removeItem: (key: string) => Promise<void>
}

/**
 * Build an AsyncStorage whose IndexedDB key is resolved LIVE per operation via
 * `getPartitionKey`, rather than closed over at construction. This lets a single
 * persister (mounted once) target the current identity's blob and follow
 * login / logout / org-switch WITHOUT remounting the React tree - reads and
 * writes simply route to whatever partition is active at call time. The `key`
 * argument the persister passes through is ignored in favour of the partition.
 */
export function createIdbAsyncStorage(
  getPartitionKey: () => string,
): IdbAsyncStorage {
  return {
    getItem: async () => {
      try {
        const db = await getDb()
        const value = await db.get(STORE, getPartitionKey())
        return (value as string | undefined) ?? null
      } catch {
        return null
      }
    },
    setItem: async (_key, value) => {
      try {
        const db = await getDb()
        await db.put(STORE, value, getPartitionKey())
      } catch {
        // Quota / private-mode / policy errors must not break the app.
      }
    },
    removeItem: async () => {
      try {
        const db = await getDb()
        await db.delete(STORE, getPartitionKey())
      } catch {
        // ignore
      }
    },
  }
}

/** Delete a single partition's cached blob (used on logout / context switch). */
export async function deletePersistedPartition(partitionKey: string): Promise<void> {
  try {
    const db = await getDb()
    await db.delete(STORE, partitionKey)
  } catch {
    // ignore
  }
}
